import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Beaker, Shield, Award, ChevronRight, Sparkles, Crown } from 'lucide-react';
import HeroSection from '../components/HeroSection';
import ProductCard from '../components/ProductCard';
import ReviewsSection from '../components/ReviewsSection';
import axios from 'axios';
import { setSeoMetadata } from '../lib/seo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/store`;

const HomePage = () => {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setSeoMetadata({
      title: 'AMINO-CHAIN Peptides | Precision Synthesized Research Compounds',
      description: 'AMINO-CHAIN Peptides delivers precision synthesized compounds for advanced laboratory studies. For research use only. Not for human consumption.',
      keywords: 'AMINO-CHAIN peptides, precision synthesized peptides, research compounds, laboratory peptide catalog, non-human research',
      canonicalPath: '/',
      jsonLd: {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: 'AMINO-CHAIN Peptides',
        description: 'Precision synthesized research peptide catalog for scientific and laboratory use.',
      },
    });
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [productsRes, categoriesRes] = await Promise.all([
          axios.get(`${API}/products`),
          axios.get(`${API}/categories`)
        ]);
        setFeaturedProducts(productsRes.data.slice(0, 8));
        setCategories(categoriesRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const features = [
    {
      icon: Beaker,
      title: 'Lab-Grade Synthesis',
      description: 'Every peptide is synthesized in ISO-certified laboratories using pharmaceutical-grade protocols.'
    },
    {
      icon: Shield,
      title: '99%+ Purity Verified',
      description: 'HPLC and Mass Spectrometry testing on every batch. Certificate of Analysis included.'
    },
    {
      icon: Sparkles,
      title: 'Laboratory Presentation',
      description: 'Premium handling standards and pristine packaging from synthesis to dispatch.'
    },
    {
      icon: Award,
      title: 'Research Support',
      description: 'Access our comprehensive research library and technical documentation.'
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="home-page">
      <HeroSection />

      <section className="relative bg-gradient-to-r from-[#2a0b45] via-[#4b1d77] to-[#2f104f] py-8" data-testid="luxury-proof-strip">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { value: '34', label: 'Premium Peptide Products' },
            { value: '9', label: 'Curated Research Categories' },
            { value: '99%+', label: 'Purity Benchmark' },
          ].map((item, index) => (
            <motion.div
              key={item.label}
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.08 }}
              className="rounded-2xl border border-[#f4e4bc]/25 bg-white/10 px-5 py-4 text-center"
              data-testid={`luxury-proof-item-${index}`}
            >
              <p className="font-heading text-3xl font-bold text-[#f4e4bc]">{item.value}</p>
              <p className="text-sm text-[#f7ead0]">{item.label}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="relative py-24 bg-gradient-to-b from-[#fbf7ff] via-[#fffefb] to-[#f7f0ff]" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#f4e4bc]/35 border border-[#e0be7a] text-[#7a4d00] text-sm font-semibold tracking-wider mb-4">
              <Crown className="w-4 h-4" />
              LAB SCIENCE STANDARD
            </span>
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-[#2e1450] mb-4" data-testid="home-luxury-section-heading">
              Research Without Compromise
            </h2>
            <p className="text-slate-600 text-lg max-w-2xl mx-auto" data-testid="home-luxury-section-copy">
              We provide the highest quality peptides for scientific research, backed by rigorous testing and transparent documentation.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="group p-6 bg-white border border-[#ead9ff] rounded-2xl hover:border-[#d9b35f] hover:shadow-xl hover:shadow-[#d9b35f]/20 transition-all duration-300"
                data-testid={`feature-${index}`}
              >
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#4b1d77] to-[#bc8b3c] flex items-center justify-center mb-5 group-hover:shadow-lg group-hover:shadow-[#d9b35f]/40 transition-all">
                  <feature.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="font-heading font-semibold text-[#2e1450] text-lg mb-3">
                  {feature.title}
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="relative py-24 bg-gradient-to-b from-[#f4ecff] via-white to-[#fffaf0]" data-testid="products-section">
        <div className="absolute inset-0 grid-bg opacity-50" />
        
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="flex flex-col md:flex-row md:items-end justify-between mb-12"
          >
            <div>
              <span className="inline-block px-4 py-2 rounded-full bg-[#f4e4bc]/35 border border-[#ddb56d] text-[#7a4d00] text-sm font-semibold tracking-wider mb-4">
                CATALOG
              </span>
              <h2 className="font-heading text-4xl md:text-5xl font-bold text-[#2e1450]">
                Featured Peptides
              </h2>
            </div>
            <Link
              to="/premium-peptides"
              data-testid="view-all-products"
              className="inline-flex items-center gap-2 text-[#6e2ea8] hover:text-[#4b1d77] font-semibold mt-4 md:mt-0 group"
            >
              View All Products
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>

          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="aspect-[3/4] bg-slate-100 rounded-2xl animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {featuredProducts.map((product, index) => (
                <ProductCard key={product.id} product={product} index={index} cardContext="homepage" />
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="relative py-24 bg-white" data-testid="categories-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-4xl font-bold text-[#2e1450] mb-4">
              Browse by Category
            </h2>
            <p className="text-slate-600 max-w-xl mx-auto">
              Explore our comprehensive range of research-grade peptides organized by application.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-4">
            {(categories.length > 0 ? categories.slice(0, 6) : [
              { name: 'Metabolic Peptides', description: 'For metabolic research applications' },
              { name: 'Repair Peptides', description: 'Tissue and cellular repair studies' },
              { name: 'Cognitive Peptides', description: 'Neurological research compounds' },
              { name: 'Growth Factors', description: 'Cell proliferation studies' },
              { name: 'Cosmetic Peptides', description: 'Dermatological research' },
              { name: 'Custom Synthesis', description: 'Tailored peptide solutions' },
            ]).map((category, index) => (
              <motion.div
                key={category.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Link
                  to={`/premium-peptides?category=${category.name}`}
                  className="group block p-6 bg-gradient-to-br from-[#faf4ff] to-[#fffdf8] border border-[#ead9ff] rounded-xl hover:border-[#d9b35f] hover:shadow-md transition-all duration-300"
                  data-testid={`category-${index}`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-heading font-semibold text-[#2e1450] group-hover:text-[#7a4d00] transition-colors">
                        {category.name}
                      </h3>
                      <p className="text-slate-500 text-sm mt-1">
                        {category.description}
                      </p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-[#7a4d00] group-hover:translate-x-1 transition-all" />
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="relative py-20 bg-gradient-to-r from-[#2a0b45] via-[#4b1d77] to-[#b68a3d]" data-testid="trust-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8 text-center">
            {[
              { value: '99%+', label: 'Purity Guaranteed' },
              { value: '10K+', label: 'Orders Shipped' },
              { value: '500+', label: 'Research Labs' },
              { value: '24hr', label: 'Dispatch Time' },
            ].map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <p className="font-heading text-4xl md:text-5xl font-bold text-white mb-2">
                  {stat.value}
                </p>
                <p className="text-[#f8ebcf]">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Reviews Section */}
      <ReviewsSection />

      <section className="relative py-32 bg-gradient-to-b from-[#fffdf8] to-[#f8f0ff]" data-testid="cta-section">
        <div className="absolute inset-0 grid-bg" />

        <div className="relative max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-[#2e1450] mb-6">
              Ready to Advance Your Research?
            </h2>
            <p className="text-slate-600 text-lg mb-10 max-w-2xl mx-auto">
              Join thousands of researchers worldwide who trust AMINO-CHAIN for their peptide research needs.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/premium-peptides"
                data-testid="cta-shop-btn"
                className="inline-flex items-center justify-center gap-3 px-8 py-4 bg-gradient-to-r from-[#f4e4bc] to-[#d6a85a] text-[#2f1650] font-heading font-bold uppercase tracking-wider rounded-full hover:shadow-xl hover:shadow-[#d6a85a]/40 transition-all"
              >
                Shop Now
                <ArrowRight className="w-5 h-5" />
              </Link>
              <Link
                to="/contact"
                data-testid="cta-contact-btn"
                className="inline-flex items-center justify-center gap-3 px-8 py-4 border-2 border-[#8a58c0] text-[#4b1d77] font-heading font-bold uppercase tracking-wider rounded-full hover:border-[#d6a85a] hover:text-[#7a4d00] transition-all"
              >
                Contact Us
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <footer className="bg-gradient-to-b from-[#1a062b] to-[#0f0419] text-white py-16 pb-24" data-testid="footer">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            <div className="md:col-span-2">
              <div className="flex items-center gap-2 mb-4">
                <img
                  src="https://customer-assets.emergentagent.com/job_35efb418-d957-4303-979f-4e5863096b08/artifacts/hzi2b2xm_amino-chain-logo-final-1.png"
                  alt="AMINO-CHAIN"
                  className="h-12 w-auto object-contain"
                />
              </div>
              <p className="text-[#d8c9e8] text-sm max-w-md mb-6">
                Premium research peptides for scientific advancement. All products are for research purposes only and not intended for human consumption.
              </p>
              <div className="flex gap-4">
                <a href="#" className="text-[#d8c9e8] hover:text-[#f4e4bc] transition-colors" data-testid="footer-social-twitter">Twitter</a>
                <a href="#" className="text-[#d8c9e8] hover:text-[#f4e4bc] transition-colors" data-testid="footer-social-linkedin">LinkedIn</a>
                <a href="#" className="text-[#d8c9e8] hover:text-[#f4e4bc] transition-colors" data-testid="footer-social-github">GitHub</a>
              </div>
            </div>

            <div>
              <h4 className="font-heading font-semibold mb-4">Products</h4>
              <ul className="space-y-2">
                <li><Link to="/premium-peptides" className="text-[#d8c9e8] hover:text-[#f4e4bc] text-sm transition-colors" data-testid="footer-products-all">All Peptides</Link></li>
                <li><Link to="/premium-peptides" className="text-[#d8c9e8] hover:text-[#f4e4bc] text-sm transition-colors" data-testid="footer-products-best">Best Sellers</Link></li>
                <li><Link to="/premium-peptides" className="text-[#d8c9e8] hover:text-[#f4e4bc] text-sm transition-colors" data-testid="footer-products-new">New Arrivals</Link></li>
                <li><Link to="/peptides-research" className="text-[#d8c9e8] hover:text-[#f4e4bc] text-sm transition-colors" data-testid="footer-products-research">Research Library</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="font-heading font-semibold mb-4">Support</h4>
              <ul className="space-y-2">
                <li><Link to="/contact" className="text-[#d8c9e8] hover:text-[#f4e4bc] text-sm transition-colors" data-testid="footer-support-contact">Contact Us</Link></li>
                <li><Link to="/faq" className="text-[#d8c9e8] hover:text-[#f4e4bc] text-sm transition-colors" data-testid="footer-support-faq">FAQs</Link></li>
                <li><Link to="/about" className="text-[#d8c9e8] hover:text-[#f4e4bc] text-sm transition-colors" data-testid="footer-support-about">About Us</Link></li>
                <li><Link to="/shipping-returns" className="text-[#d8c9e8] hover:text-[#f4e4bc] text-sm transition-colors" data-testid="footer-support-shipping">Shipping & Returns</Link></li>
                <li><a href="mailto:support@amino-chain.com" className="text-[#d8c9e8] hover:text-[#f4e4bc] text-sm transition-colors" data-testid="footer-support-email">support@amino-chain.com</a></li>
              </ul>
            </div>
          </div>

          {/* Legal Links */}
          <div className="pt-6 mb-8 border-t border-[#4b2b6c]">
            <div className="flex flex-wrap justify-center gap-6 text-sm">
              <Link to="/privacy-policy" className="text-[#d8c9e8] hover:text-[#f4e4bc] transition-colors">Privacy Policy</Link>
              <Link to="/terms-conditions" className="text-[#d8c9e8] hover:text-[#f4e4bc] transition-colors">Terms & Conditions</Link>
              <Link to="/accessibility" className="text-[#d8c9e8] hover:text-[#f4e4bc] transition-colors">Accessibility</Link>
              <Link to="/compliance" className="text-[#d8c9e8] hover:text-[#f4e4bc] transition-colors">Compliance</Link>
              <Link to="/shipping-returns" className="text-[#d8c9e8] hover:text-[#f4e4bc] transition-colors">Shipping & Returns</Link>
            </div>
          </div>

          <div className="pt-6 border-t border-[#4b2b6c] flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-[#ccb7dd] text-sm" data-testid="footer-copyright">
              © 2026 AMINO-CHAIN. All rights reserved.
            </p>
            <p className="text-[#f4e4bc]/90 text-xs font-mono tracking-wider text-center" data-testid="footer-compliance-note">
              FOR RESEARCH USE ONLY • NOT FOR HUMAN CONSUMPTION
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
